import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { Loader2, FileDown, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditLog = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const q = () => {
    const p = [];
    if (action) p.push(`action=${action}`);
    if (start) p.push(`start=${start}`);
    if (end) p.push(`end=${end}`);
    return p.length ? `?${p.join('&')}` : '';
  };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/audit-logs${q()}`).then((r) => { setLogs(r.data.logs || []); setActions(r.data.actions || []); }).catch(() => toast.error(t('audit.loadFailed'))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, start, end]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/audit-logs/export${q()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'audit_logs.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('audit.exportFailed')); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-indigo-500" /> {t('audit.title')}</h1>
          <button onClick={exportCsv} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2" data-testid="audit-export-btn"><FileDown className="w-4 h-4" /> {t('common.exportCsv')}</button>
        </div>
        <div className="flex gap-2 flex-wrap items-center bg-white border border-slate-200 rounded-lg p-3" data-testid="audit-filters">
          <select value={action} onChange={(e) => setAction(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="audit-action-filter">
            <option value="">{t('audit.allActions')}</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="audit-start" />
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="audit-end" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}</div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="audit-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-5 py-2">{t('audit.time')}</th><th className="px-3 py-2">{t('audit.actor')}</th><th className="px-3 py-2">{t('audit.action')}</th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-400">{t('audit.empty')}</td></tr>}
                {logs.map((l, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid="audit-row">
                    <td className="px-5 py-2 text-slate-500 whitespace-nowrap">{new Date(l.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2 text-slate-700">{l.actorName}</td>
                    <td className="px-3 py-2"><span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{l.action}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AuditLog;
